<script setup lang="ts">
import { TooltipArrow, TooltipContent, TooltipPortal, TooltipRoot, TooltipTrigger } from 'reka-ui'

/**
 * Tooltips explain an affordance; they never carry information the user needs
 * to complete a task, because touch and keyboard users may never see them.
 * Icon-only buttons still require their own `aria-label`.
 *
 * Requires an `ETooltipProvider` somewhere above it (once, at the app shell).
 */

withDefaults(
  defineProps<{
    content: string
    side?: 'top' | 'right' | 'bottom' | 'left'
    align?: 'start' | 'center' | 'end'
    delayMs?: number
    disabled?: boolean
    /** Keyboard hint rendered after the text, e.g. `⌘K`. */
    shortcut?: string
  }>(),
  { side: 'top', align: 'center', delayMs: 400, shortcut: '' },
)
</script>

<template>
  <TooltipRoot :delay-duration="delayMs" :disabled="disabled">
    <TooltipTrigger as-child>
      <slot />
    </TooltipTrigger>
    <TooltipPortal>
      <TooltipContent class="e-tooltip" :side="side" :align="align" :side-offset="6">
        <span>{{ content }}</span>
        <span v-if="shortcut !== ''" class="e-tooltip__shortcut">{{ shortcut }}</span>
        <TooltipArrow class="e-tooltip__arrow" :width="10" :height="5" />
      </TooltipContent>
    </TooltipPortal>
  </TooltipRoot>
</template>

<style>
.e-tooltip {
  z-index: var(--erpx-z-toast);
  display: inline-flex;
  align-items: center;
  gap: var(--erpx-space-2);
  max-width: 20rem;
  padding: var(--erpx-space-1) var(--erpx-space-2);
  background-color: var(--erpx-color-surface-inverted);
  color: var(--erpx-color-text-on-inverted);
  border-radius: var(--erpx-radius-sm);
  font-size: var(--erpx-text-xs);
  line-height: var(--erpx-leading-snug);
  box-shadow: var(--erpx-shadow-2);
}

.e-tooltip__shortcut {
  color: var(--erpx-color-text-on-inverted-muted);
  font-family: var(--erpx-font-mono);
}

.e-tooltip__arrow {
  fill: var(--erpx-color-surface-inverted);
}
</style>
